import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { LogOut } from "lucide-react";

import logoElmv from "@/assets/logo-elmv-bandeau.png";
import { Button } from "@/components/ui/button";
import { connexion, deconnexion, nomAffiche, utilisateurValide } from "@/auth/oidc";
import { useDeconnexionAilleurs, useUtilisateur } from "@/auth/useUtilisateur";
import { useGestionnaire } from "@/features/moi/api";

/**
 * Mise en page des écrans réservés aux utilisateurs connectés — sans segment d'URL :
 * /planning reste /planning.
 *
 * Le garde s'exécute avant tout rendu : sans session valide, on part vers Keycloak
 * sans jamais montrer l'écran ni tenter un appel voué au 401.
 */
export const Route = createFileRoute("/_connecte")({
  beforeLoad: async ({ location }) => {
    if (!(await utilisateurValide())) await connexion(location.href);
  },
  component: Connecte,
});

function Connecte() {
  const user = useUtilisateur();
  const gestionnaire = useGestionnaire();
  useDeconnexionAilleurs();

  return (
    <div className="flex h-svh flex-col">
      <header className="flex h-14 shrink-0 items-center gap-6 bg-bandeau px-5 text-bandeau-foreground">
        <Link to="/" className="flex items-center gap-3" aria-label="Ultimate — accueil">
          {/* Le logo d'elmv, détouré : fond rendu transparent, « elmV » passé en blanc pour le bandeau. */}
          <img src={logoElmv} alt="elmv" className="h-8 w-auto" />
          <span className="h-6 w-px bg-white/25" aria-hidden />
          <span className="text-lg font-bold tracking-wide">Ultimate</span>
        </Link>
        <nav className="flex items-center gap-1 text-sm">
          <LienMenu to="/planning">Planning</LienMenu>
          {gestionnaire && (
            <>
              <LienMenu to="/a-gerer">À gérer</LienMenu>
              <LienMenu to="/lieux">Lieux</LienMenu>
              <LienMenu to="/types-seance">Types de séance</LienMenu>
              <LienMenu to="/utilisateurs">Utilisateurs</LienMenu>
              <LienMenu to="/rapports">Rapports</LienMenu>
            </>
          )}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {user && (
            <Link to="/profil" title="Mon profil" className={LIEN_BANDEAU}>
              {nomAffiche(user)}
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-bandeau-foreground hover:bg-white/10 hover:text-bandeau-foreground"
            onClick={() => void deconnexion()}
          >
            <LogOut />
            Déconnexion
          </Button>
        </div>
      </header>
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

/** Un lien du bandeau ; le routeur pose `data-status="active"` sur celui de la page courante. */
const LIEN_BANDEAU =
  "rounded-md px-3 py-1.5 text-sm text-bandeau-muted transition-colors hover:bg-white/10 hover:text-bandeau-foreground " +
  "data-[status=active]:bg-bandeau-actif data-[status=active]:font-semibold data-[status=active]:text-bandeau-foreground";

function LienMenu({ to, children }: { to: "/planning" | "/a-gerer" | "/lieux" | "/types-seance" | "/utilisateurs" | "/rapports"; children: string }) {
  return (
    <Link to={to} className={LIEN_BANDEAU}>
      {children}
    </Link>
  );
}
